import { useEffect } from 'react';

/**
 * Custom hook for keyboard shortcuts
 * @param {Object} shortcuts - Object mapping key combinations to callbacks
 * @param {Array} dependencies - Dependencies array for the effect
 * 
 * @example
 * useKeyboardShortcuts({
 *   '/': () => navigate('/search'),
 *   'n': () => navigate('/create-post'),
 *   'Escape': () => closeModal()
 * });
 */
export const useKeyboardShortcuts = (shortcuts, dependencies = []) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when user is typing in inputs
      const target = e.target;
      const isInput = target.tagName === 'INPUT' || 
                      target.tagName === 'TEXTAREA' || 
                      target.isContentEditable ||
                      target.closest('[contenteditable="true"]');
      
      // Allow Escape to work even in inputs (for closing modals)
      if (isInput && e.key !== 'Escape') {
        return;
      }

      // Check for modifier keys
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey || e.shiftKey;
      
      // Build key string
      let keyString = '';
      if (e.ctrlKey || e.metaKey) keyString += 'ctrl+';
      if (e.altKey) keyString += 'alt+';
      if (e.shiftKey) keyString += 'shift+';
      keyString += e.key.toLowerCase();

      // Check for exact match first
      if (shortcuts[keyString]) {
        e.preventDefault();
        shortcuts[keyString](e);
        return;
      }

      // Check for key without modifiers (if no modifier was pressed)
      if (!hasModifier && shortcuts[e.key]) {
        e.preventDefault();
        shortcuts[e.key](e);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [shortcuts, ...dependencies]);
};

export default useKeyboardShortcuts;

